import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button, Input } from '../ui/UIComponents';
import { useAuth } from '../../contexts/AuthContext';
import GoogleIcon from '../ui/GoogleIcon';
import ApexScholarLogo from '../ui/ApexScholarLogo';
import { stashPendingConsent } from '../../constants/consent';

const RETURN_KEY = 'apex.auth.returnTo';

// Same-origin app paths only: anything else in storage or state is ignored.
const safePath = (p) => (typeof p === 'string' && p.startsWith('/') && !p.startsWith('//') && !p.startsWith('/login') ? p : null);
const pathOf = (loc) => (loc?.pathname ? safePath(`${loc.pathname}${loc.search || ''}${loc.hash || ''}`) : null);
const readReturnTo = () => { try { return safePath(sessionStorage.getItem(RETURN_KEY)); } catch { return null; } };
const stashReturnTo = (p) => { try { sessionStorage.setItem(RETURN_KEY, p); } catch { /* private window */ } };
const clearReturnTo = () => { try { sessionStorage.removeItem(RETURN_KEY); } catch { /* private window */ } };

export function LoginPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, loading: authLoading, signInWithGoogle, signUpWithEmail, signInWithEmail, resetPassword } = useAuth();

    const [isSignUp, setIsSignUp] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [fullName, setFullName] = useState('');
    // Both unticked by default. A pre-ticked box is not consent, and terms
    // acceptance in particular has to be an affirmative act.
    const [emailOptIn, setEmailOptIn] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Where to go after signing in. Production Google sign-in is a full-page
    // redirect that comes back to /login with fresh history state, so the
    // destination is also parked in sessionStorage before it leaves. Without
    // that, every Google sign-in landed on "/" and the Start check's "Sign in to
    // save the ones you missed" never got back to /start to save them.
    const from = pathOf(location.state?.from) || readReturnTo() || "/";

    // Navigate when user is authenticated
    useEffect(() => {
        if (user && !isLoading) {
            clearReturnTo();
            navigate(from, { replace: true });
        }
    }, [user, isLoading, navigate, from]);

    // Reset loading state when user changes
    useEffect(() => {
        if (user) {
            setIsLoading(false);
        }
    }, [user]);

    // Show loading spinner during auth initialization (e.g., processing redirect result)
    if (authLoading) {
        return (
            <div className="min-h-screen bg-base-950 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-content-muted border-t-transparent mx-auto mb-4"></div>
                    <p className="text-content-muted text-sm">Signing you in...</p>
                </div>
            </div>
        );
    }

    const handleAuthAction = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            if (isSignUp) {
                if (!fullName.trim()) {
                    setError("Please enter your full name.");
                    setIsLoading(false);
                    return;
                }
                if (!acceptedTerms) {
                    setError("Please accept the Terms of Service and Privacy Policy to create an account.");
                    setIsLoading(false);
                    return;
                }
                await signUpWithEmail(email, password, fullName, emailOptIn);
            } else {
                await signInWithEmail(email, password);
            }
        } catch (err) {
            setError(err.message);
            setIsLoading(false);
        }
    };

    const handleForgotPassword = async () => {
        if (!email.trim()) {
            setError('Please enter your email address first.');
            return;
        }
        setError('');
        setSuccessMessage('');
        setIsLoading(true);
        try {
            await resetPassword(email);
            setSuccessMessage('Password reset email sent. Check your inbox. If you don\'t see it, check your junk/spam folder.');
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        // Terms have to be accepted before an account can exist, and with Google
        // we can't tell new from returning until after auth — so the box is
        // required here too. For a returning user that's one extra click.
        if (isSignUp && !acceptedTerms) {
            setError("Please accept the Terms of Service and Privacy Policy to continue.");
            return;
        }
        // Production Google sign-in is a redirect: the page navigates away and
        // reloads, so these choices can't be passed as arguments. AuthContext
        // reads them back when it creates the user document.
        stashPendingConsent({ acceptedTerms: acceptedTerms || !isSignUp, emailOptIn });
        stashReturnTo(from);
        setIsLoading(true);
        try {
            await signInWithGoogle();
        } catch (err) {
            setError(err.message);
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-base-950 flex">
            {/* Left Side - Clean value prop */}
            <div className="hidden md:flex md:w-1/2 lg:w-[55%] flex-col justify-center px-12 lg:px-20 bg-base-900 border-r border-border">
                <div className="max-w-lg">
                    <div className="flex items-center gap-3 mb-10">
                        <ApexScholarLogo className="w-9 h-9" />
                        <span className="text-xl font-display font-semibold text-content-primary">Apex Scholar</span>
                    </div>

                    <h2 className="text-3xl lg:text-4xl font-display font-semibold text-content-primary leading-tight mb-4">
                        Study for your AP exams.
                    </h2>
                    <p className="text-lg text-content-secondary leading-relaxed mb-10">
                        AI tutors, practice tests, flashcards, and a smart study scheduler — all in one place for every AP subject.
                    </p>

                    <div className="space-y-4 text-sm text-content-muted">
                        <div className="flex items-center gap-3">
                            <div className="w-1 h-1 rounded-full bg-content-muted flex-shrink-0" />
                            <span>AI-powered tutoring for every AP subject</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-1 h-1 rounded-full bg-content-muted flex-shrink-0" />
                            <span>Exam-style practice with instant feedback</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-1 h-1 rounded-full bg-content-muted flex-shrink-0" />
                            <span>Personalized study schedules that adapt to you</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Side - Login Form */}
            <div className="w-full md:w-1/2 lg:w-[45%] flex items-center justify-center p-6 md:p-8 lg:p-12">
                <div className="w-full max-w-sm">
                    {/* Mobile Logo */}
                    <div className="md:hidden flex items-center gap-3 mb-10">
                        <ApexScholarLogo className="w-8 h-8" />
                        <span className="text-lg font-display font-semibold text-content-primary">Apex Scholar</span>
                    </div>

                    <div className="mb-8">
                        <h1 className="text-xl font-display font-semibold text-content-primary mb-1">
                            {isSignUp ? "Create an account" : "Sign in"}
                        </h1>
                        <p className="text-sm text-content-muted">
                            {isSignUp ? "Get started with Apex Scholar." : "Welcome back to Apex Scholar."}
                        </p>
                    </div>

                    <form onSubmit={handleAuthAction} className="space-y-4">
                        {isSignUp && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.2 }}
                            >
                                <label htmlFor="name" className="block text-sm font-medium mb-1.5 text-content-secondary">Full name</label>
                                <Input
                                    id="name"
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    className="h-10"
                                    placeholder="Your full name"
                                    required
                                />
                            </motion.div>
                        )}
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium mb-1.5 text-content-secondary">Email</label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="h-10"
                                placeholder="you@example.com"
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-content-secondary mb-1.5">Password</label>
                            <Input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="h-10"
                                placeholder="Your password"
                                required
                            />
                            {/* "Forgot password?" sits below the password input on its
                                own line so the tap target has room to breathe (Apple
                                HIG: ≥44pt, Lighthouse flagged the prior inline placement
                                because the 88×16px button overlapped the password input
                                tap area). Negative right margin keeps the visual edge
                                aligned with the input border. */}
                            {!isSignUp && (
                                <div className="flex justify-end mt-1.5">
                                    <button
                                        type="button"
                                        onClick={handleForgotPassword}
                                        className="inline-flex items-center text-xs text-content-muted hover:text-content-primary transition-colors px-2 py-1.5 -mr-2 rounded min-h-[28px]"
                                        disabled={isLoading}
                                    >
                                        Forgot password?
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Sign-up consent. Both unticked by default: a pre-checked
                            box is not consent, and most users here are minors.
                            Terms are required; email is optional. */}
                        {isSignUp && (
                            <div className="space-y-2.5">
                                <label className="flex items-start gap-2.5 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={acceptedTerms}
                                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                                        disabled={isLoading}
                                        className="h-4 w-4 mt-0.5 text-content-primary bg-base-800 border-border-strong rounded focus:ring-content-muted"
                                    />
                                    <span className="text-xs text-content-secondary leading-relaxed">
                                        I agree to the{' '}
                                        <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary-400 underline">
                                            Terms of Service
                                        </a>{' '}
                                        and{' '}
                                        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary-400 underline">
                                            Privacy Policy
                                        </a>. <span className="text-content-muted">(required)</span>
                                    </span>
                                </label>
                                <label className="flex items-start gap-2.5 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={emailOptIn}
                                        onChange={(e) => setEmailOptIn(e.target.checked)}
                                        disabled={isLoading}
                                        className="h-4 w-4 mt-0.5 text-content-primary bg-base-800 border-border-strong rounded focus:ring-content-muted"
                                    />
                                    <span className="text-xs text-content-secondary leading-relaxed">
                                        Email me occasional study reminders and updates.{' '}
                                        <span className="text-content-muted">
                                            Optional — change it any time in Settings.
                                        </span>
                                    </span>
                                </label>
                            </div>
                        )}

                        {successMessage && (
                            <div className="p-3 bg-success-900 border border-success-500/30 rounded-md">
                                <p className="text-sm text-success-400">{successMessage}</p>
                            </div>
                        )}

                        {error && (
                            <div className="p-3 bg-error-900 border border-error-500/30 rounded-md">
                                <p className="text-sm text-error-400">{error}</p>
                            </div>
                        )}

                        <Button
                            className="w-full h-10"
                            type="submit"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <div className="w-4 h-4 border-2 border-base-950 border-t-transparent rounded-full animate-spin" />
                            ) : (
                                isSignUp ? "Create account" : "Sign in"
                            )}
                        </Button>
                    </form>

                    <div className="my-6 flex items-center">
                        <hr className="flex-grow border-t border-border" />
                        <span className="mx-3 text-xs text-content-muted uppercase tracking-wider">or</span>
                        <hr className="flex-grow border-t border-border" />
                    </div>

                    <Button
                        variant="outline"
                        className="w-full h-10"
                        onClick={handleGoogleSignIn}
                        disabled={isLoading || (isSignUp && !acceptedTerms)}
                    >
                        <GoogleIcon className="w-4 h-4 mr-2" />
                        Continue with Google
                    </Button>

                    <div className="mt-4 text-center">
                        <button
                            type="button"
                            onClick={() => navigate('/ai-tutors')}
                            disabled={isLoading}
                            className="text-sm text-content-muted hover:text-content-primary transition-colors disabled:opacity-50"
                        >
                            Continue as guest
                        </button>
                        <p className="mt-1 text-xs text-content-disabled">
                            Try the AI Tutors without an account. Your chats stay on this device.
                        </p>
                    </div>

                    <p className="mt-6 text-center text-sm text-content-muted">
                        {isSignUp ? "Already have an account?" : "Don't have an account?"}
                        <button
                            onClick={() => setIsSignUp(!isSignUp)}
                            className="ml-1 font-medium text-content-primary hover:underline transition-colors duration-150"
                        >
                            {isSignUp ? "Sign in" : "Sign up"}
                        </button>
                    </p>

                    <div className="mt-8 flex items-center justify-center gap-1.5 text-xs text-content-disabled">
                        <Shield className="w-3 h-3" strokeWidth={1.5} />
                        <span>Secured with Firebase Authentication</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
